import React, { useState } from 'react';
import {
  Box,
  Container,
  Fab,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Upload as UploadIcon
} from '@mui/icons-material';

import { useDashboard } from '../hooks/useDashboard';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useAuth } from '../contexts/AuthContext';
import DashboardHeader from './DashboardHeader';
import SearchResults from './SearchResults';
import DashboardGrid from './DashboardGrid';
import EditManager from './EditManager';
import TabBar from './TabBar';
import TabPanel from './TabPanel';
import TabEditDialog from './TabEditDialog';
import ThinkBeyondLogo from '../assets/images/thumbnail_Think Beyond AI-favicon.jpg';

const DashboardLayout: React.FC = () => {
  const { user } = useAuth();
  const {
    config,
    isLoading,
    isDirty,
    error,
    editMode,
    toggleEditMode,
    saveConfig,
    searchQuery,
    searchResults,
    activeTab,
    setActiveTab,
    createTab,
    updateTab,
    deleteTab,
    reorderTabs,
    updateConfig
  } = useDashboard();

  // State for import/export functionality
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importError, setImportError] = useState<string>('');
  const [exportSuccess, setExportSuccess] = useState(false);

  useKeyboardShortcuts();

  const [showSaveSuccess, setShowSaveSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [tabDialogOpen, setTabDialogOpen] = React.useState(false);
  const [tabDialogMode, setTabDialogMode] = React.useState<'create' | 'edit'>('create');
  const [editingTab, setEditingTab] = React.useState<any>(null);

  // Handle save action with better error handling
  const handleSave = async () => {
    if (!isDirty) return;
    
    try {
      await saveConfig();
      setShowSaveSuccess(true);
      // Clear any previous errors
      setShowError(false);
    } catch (err) {
      console.error('Save failed:', err);
      setShowError(true);
    }
  };

  // Listen for custom keyboard shortcut events
  React.useEffect(() => {
    const handleSaveShortcut = () => {
      if (isDirty) {
        handleSave();
      }
    };

    document.addEventListener('dashboard:save', handleSaveShortcut);
    return () => {
      document.removeEventListener('dashboard:save', handleSaveShortcut);
    };
  }, [isDirty, handleSave]);

  // Tab handling functions
  const handleTabCreate = () => {
    setTabDialogMode('create');
    setEditingTab(null);
    setTabDialogOpen(true);
  };

  const handleTabEdit = (tab: any) => {
    setTabDialogMode('edit');
    setEditingTab(tab);
    setTabDialogOpen(true);
  };

  const handleTabDelete = async (tabId: string) => {
    try {
      await deleteTab(tabId);
    } catch (error) {
      console.error('Failed to delete tab:', error);
    }
  };

  const handleTabReorder = (sourceIndex: number, destIndex: number) => {
    reorderTabs(sourceIndex, destIndex);
  };

  const handleTabSubmit = async (tabData: any) => {
    if (tabDialogMode === 'create') {
      await createTab(tabData);
    } else {
      await updateTab(editingTab.id, tabData);
    }
    setTabDialogOpen(false);
  };

  const handleTabChange = async (tabId: string) => {
    try {
      await setActiveTab(tabId);
    } catch (error) {
      console.error('Failed to change tab:', error);
    }
  };

  // Check if user has admin/superuser privileges
  const isAdminOrSuperuser = user?.role === 'admin' || user?.role === 'superuser';
  // Export configuration as JSON
  const handleExportConfig = () => {
    if (!config) return;
    
    try {
      const configToExport = {
        ...config,
        metadata: {
          ...config.metadata,
          exportedAt: new Date().toISOString(),
          exportedBy: user?.email
        }
      };
      const dataStr = JSON.stringify(configToExport, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard-config-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportSuccess(true);
      setShowExportDialog(false);
    } catch (error) {
      console.error('Failed to export configuration:', error);
    }
  };

  // Import configuration from JSON file
  const handleImportConfig = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportError('');

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setImportError('Please select a valid JSON file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const fileContent = e.target?.result as string;
        const importedConfig = JSON.parse(fileContent);

        // Basic validation
        if (!importedConfig || typeof importedConfig !== 'object') {
          setImportError('Invalid configuration file format.');
          return;
        }

        // Validate required fields
        const requiredFields = ['layout', 'theme'];
        const missingFields = requiredFields.filter(field => !(field in importedConfig));
        if (missingFields.length > 0) {
          setImportError(`Missing required fields: ${missingFields.join(', ')}`);
          return;
        }

        // Update metadata to indicate import
        const configWithMetadata = {
          ...importedConfig,
          metadata: {
            ...importedConfig.metadata,
            importedAt: new Date().toISOString(),
            importedBy: user?.email,
            lastModified: new Date().toISOString()
          }
        };

        // Apply the configuration
        await updateConfig(configWithMetadata);
        setShowImportDialog(false);
        
        // Reset file input
        event.target.value = '';
      } catch (error) {
        console.error('Failed to import configuration:', error);
        setImportError('Failed to parse configuration file. Please check the file format.');
      }
    };

    reader.readAsText(file);
  };

  if (!config && isLoading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: 9999 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!config) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Failed to load dashboard configuration. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  const showSearchResults = searchQuery && searchResults.length > 0;

  return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'background-color 0.3s ease, color 0.3s ease',
        }}
      >
        {/* Header */}
        <DashboardHeader
          title={config.layout.headerTitle}
          icon={config.layout.headerIcon}
          editMode={editMode}
          onToggleEditMode={toggleEditMode}
        />

        {/* Tab Navigation */}
        {config.tabs && config.tabs.length > 0 && (
          <TabBar
            tabs={config.tabs}
            activeTabId={config.activeTabId || config.tabs[0].id}
            onTabChange={handleTabChange}
            onTabCreate={handleTabCreate}
            onTabEdit={handleTabEdit}
            onTabDelete={handleTabDelete}
            onTabReorder={handleTabReorder}
            editMode={editMode}
          />
        )}

        {/* Main Content */}
        <Box 
          sx={{ 
            px: { xs: 1, sm: 2, md: 3 }, 
            py: 3, 
            pb: 8, // Add bottom padding to prevent footer overlap
            width: '100%', 
            maxWidth: '100vw',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Search Results or Tab Content */}
          {showSearchResults ? (
            <SearchResults />
          ) : activeTab ? (
            <TabPanel
              tab={activeTab}
              isActive={true}
            />
          ) : (
            // Fallback for legacy structure
            <DashboardGrid
              groups={config.groups}
              layout={config.layout}
              theme={config.theme}
              editMode={editMode}
            />
          )}
        </Box>

        {/* Floating Action Buttons */}
        {editMode && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 40,
              right: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              zIndex: 1000,
            }}
          >
            {isDirty && (
              <Fab
                color="primary"
                aria-label="save changes"
                onClick={handleSave}
                size="medium"
                sx={{ mb: 1 }}
              >
                <SaveIcon />
              </Fab>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Fab
                aria-label="add new group"
                onClick={() => {
                  const event = new CustomEvent('dashboard:new-group');
                  document.dispatchEvent(event);
                }}
                size="small"
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light'
                  },
                  color: 'white'
                }}
              >
                <AddIcon />
              </Fab>

              {/* Import/Export buttons for admin and superuser only */}
              {isAdminOrSuperuser && (
                <>
                  <Tooltip title="Export Configuration" placement="left">
                    <Fab
                      aria-label="export configuration"
                      onClick={() => setShowExportDialog(true)}
                      size="small"
                      sx={{ 
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.light'
                        },
                        color: 'white'
                      }}
                    >
                      <DownloadIcon />
                    </Fab>
                  </Tooltip>
                  
                  <Tooltip title="Import Configuration" placement="left">
                    <Fab
                      aria-label="import configuration"
                      onClick={() => setShowImportDialog(true)}
                      size="small"
                      sx={{ 
                          backgroundColor: 'secondary.main',
                        '&:hover': {
                          backgroundColor: 'secondary.light'
                        },
                        color: 'white'
                       }}
                    >
                      <UploadIcon />
                    </Fab>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        )}

        {/* Loading Backdrop */}
        <Backdrop
          open={isLoading}
          sx={{ color: '#fff', zIndex: 9998 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* Success Snackbar */}
        <Snackbar
          open={showSaveSuccess}
          autoHideDuration={3000}
          onClose={() => setShowSaveSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={() => setShowSaveSuccess(false)}
            severity="success"
            variant="filled"
          >
            Dashboard saved successfully!
          </Alert>
        </Snackbar>

        {/* Error Snackbar */}
        <Snackbar
          open={showError || !!error}
          autoHideDuration={6000}
          onClose={() => setShowError(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={() => setShowError(false)}
            severity="error"
            variant="filled"
          >
            {error || 'An error occurred. Please try again.'}
          </Alert>
        </Snackbar>


        {/* Edit Manager for Edit Dialogs */}
        <EditManager editMode={editMode} />

        {/* Tab Edit Dialog */}
        <TabEditDialog
          open={tabDialogOpen}
          onClose={() => setTabDialogOpen(false)}
          onSubmit={handleTabSubmit}
          tab={editingTab}
          mode={tabDialogMode}
          isLoading={isLoading}
        />

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            py: 1,
            px: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            zIndex: 999,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {/* Empty space for balance - Left side */}
            <Box sx={{ width: 100 }} />

            {/* Powered by Think Beyond - Center */}
            <Box
              component="a"
              href="https://thinkbeyond.ai/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer',
                '&:hover .footer-text, &:hover .footer-logo': {
                  opacity: 1,
                }
              }}
            >
              <Typography 
                variant="caption" 
                color="text.secondary"
                className="footer-text"
                sx={{ 
                  fontSize: '0.75rem',
                  opacity: 0.7,
                  transition: 'opacity 0.2s ease'
                }}
              >
                Powered by Think Beyond
              </Typography>
              <Box
                component="img"
                src={ThinkBeyondLogo}
                alt="ThinkBeyond AI"
                className="footer-logo"
                sx={{
                  height: 20,
                  opacity: 0.7,
                  transition: 'opacity 0.2s ease'
                }}
              />
            </Box>

            {/* Build Info - Right side */}
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                fontSize: '0.7rem',
                opacity: 0.8,
                fontFamily: 'monospace',
                fontWeight: 500
              }}
            >
              {`v${process.env.REACT_APP_RELEASE_VERSION || ' local'}`}
              {` • Build ${process.env.REACT_APP_BUILD_NUMBER || ' 0'}`}
            </Typography>
          </Box>
        </Box>

        {/* Export Configuration Dialog */}
        <Dialog
          open={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Export Dashboard Configuration</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will download your current dashboard configuration as a JSON file. 
              You can use this file to backup your settings or import them into another instance.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleExportConfig} variant="contained" startIcon={<DownloadIcon />}>
              Download Configuration
            </Button>
          </DialogActions>
        </Dialog>

        {/* Import Configuration Dialog */}
        <Dialog
          open={showImportDialog}
          onClose={() => setShowImportDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Import Dashboard Configuration</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Upload a JSON configuration file to replace your current dashboard settings. 
              This action cannot be undone - make sure to export your current configuration first if you want to keep it.
            </DialogContentText>
            {importError && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {importError}
              </Alert>
            )}
            <Box sx={{ mt: 2 }}>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleImportConfig}
                style={{ display: 'none' }}
                id="import-config-input"
              />
              <label htmlFor="import-config-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ mt: 1 }}
                >
                  Choose Configuration File
                </Button>
              </label>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Export Success Snackbar */}
        <Snackbar
          open={exportSuccess}
          autoHideDuration={4000}
          onClose={() => setExportSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={() => setExportSuccess(false)} severity="success" sx={{ width: '100%' }}>
            Configuration exported successfully!
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default DashboardLayout;